import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { Button, getInput, Modal, Spinner } from "../ui";

import {
  FETCH_USER as fetchUser,
  FETCH_USERS as fetchUsers,
} from "../../graphql/query/User";
import { FETCH_LIST } from "../../graphql/query/List";
import { SHARE_LIST } from "../../graphql/mutation/List";
import { useFormik } from "formik";
import { Fragment, useEffect, useState } from "react";

const ShareSelectListModal = props => {
  const { children, id } = props || {};
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Always fetch current user (needed for filtering)
  const { data: currentUserData } = useQuery(fetchUser());
  const { user: currentUser } = currentUserData || {};

  // Lazy load list details
  const [fetchListDetails, { data: listData, loading: listLoading }] =
    useLazyQuery(FETCH_LIST({ sharing: true }), {
      variables: { id: +id },
      fetchPolicy: "network-only", // Always get fresh data when modal opens
    });
  const sharedWithUsers = listData?.list?.sharedWith || [];

  // Lazy load users
  const [fetchUsersList, { data: userData, loading: usersLoading }] =
    useLazyQuery(fetchUsers(), {
      variables: { input: {} },
    });
  const { users } = userData || {};
  const { edges = [] } = users || {};

  const [shareList, { loading }] = useMutation(SHARE_LIST);

  // Fetch data when modal opens
  useEffect(() => {
    if (isModalOpen && id) {
      fetchListDetails();
      fetchUsersList();
    }
  }, [isModalOpen, id, fetchListDetails, fetchUsersList]);

  const formik = useFormik({
    initialValues: { receivers: null },
    enableReinitialize: true,
    initialErrors: { errorSharing: false },
  });

  // Filter out current user and already shared users
  const alreadySharedUserIds = sharedWithUsers.map(u => u.id);

  const inputs = [
    {
      name: "receivers",
      type: "multi-select",
      isMulti: true,
      placeholder: "Select...",
      options: [
        ...edges
          .filter(
            item =>
              item.id !== currentUser?.id &&
              !alreadySharedUserIds.includes(item.id),
          )
          .map(item => ({
            label: item?.meta?.name || item.email,
            value: item?.id,
          })),
      ],
    },
  ];

  const renderInputs = inputs?.map((item, index) => {
    const inputProps = {
      ...item,
      formik,
    };

    return <Fragment key={index}>{getInput(inputProps)}</Fragment>;
  });

  const onClose = () => {
    formik.resetForm();
    setIsModalOpen(false);
  };

  const onOpen = () => {
    setIsModalOpen(true);
  };

  const modalProps = {
    title: "Share Select List",
    scale: "sm",
    description: "Select users to whom you want to share this Select List",
    closeOnBackdrop: false,
    onClose,
    onOpen,
    body: ({ closeModal }) => {
      const handleShare = () => {
        shareList({
          variables: {
            input: {
              listId: +id,
              userIds:
                formik.values?.receivers?.map(({ value }) => +value) || [],
            },
          },
          update: (cache, { data }) => {
            const { shareList: { success } = {} } = data;
            if (success) {
              closeModal();
            } else {
              formik.setErrors({ errorSharing: true });
            }
          },
        });
      };

      // Show loading state while fetching data
      if (listLoading || usersLoading) {
        return (
          <div className="flex items-center justify-center py-8">
            <Spinner />
            <div className="text-gray-500">Loading...</div>
          </div>
        );
      }

      return (
        <>
          {/* Input for adding new users */}
          <div className="mb-5 mt-5">{renderInputs}</div>

          {/* Display currently shared users */}
          {sharedWithUsers.length > 0 && (
            <div className="mb-4">
              <p className="mb-2 text-sm font-medium text-gray-700">
                Currently shared with:
              </p>
              <div className="flex flex-wrap gap-2">
                {sharedWithUsers.map(user => (
                  <span
                    key={user.id}
                    className="inline-flex max-w-full break-all rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-800"
                  >
                    {user?.meta?.name || user.email}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row">
            <Button
              label={"Cancel"}
              action={"default"}
              onClick={() => {
                closeModal?.();
              }}
            />
            <Button
              label={
                loading
                  ? "Sharing"
                  : formik?.errors?.errorSharing
                    ? "Error Sharing Select List"
                    : "Share"
              }
              disabled={loading || formik?.errors?.errorSharing}
              onClick={handleShare}
            />
          </div>
        </>
      );
    },
  };

  return <Modal {...modalProps}>{children}</Modal>;
};

export default ShareSelectListModal;
